import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO/SEO";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import * as _ from "lodash";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "antd";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";

import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useAuth } from "@/_zustand/auth";
import { useOrder } from "@/_zustand/order";
import { formatMoney } from "@/lib";
import { currentDate } from "@/lib";
import { processDate } from "@/lib";
import { generarBuyOrderMP } from "@/lib/mpCheckout";
import { hayOfertaQueExcluyeCupon } from "@/lib/exclusionCupon";
import { useOneClickComercio } from "@/hooks/useOneClickComercio";
import {
  filtrarTarjetasPorComercio,
  esTarjetaValidaParaComercio,
} from "@/lib/oneclickCards";

import { ProductsComponent } from "./components/ProductsComponent";
import { WebPayButton } from "./components/WebPayButton";
import { OneClickButton } from "./components/OneClickButton";
import { MercadoPagoButton } from "./components/MercadoPagoButton";
import { AddCardOneClick } from "./components/AddCardOneClick";
import { CardOneClick } from "./components/CardOneClick";
import { useValidStockBolsa } from "@/hooks/useProductos";
import { useGetOneClickUser } from "@/hooks/useAuth";
import { useGetTransbankMethods } from "@/hooks/useMercadoPago";
import { CodigoDeDescuento } from "@/hooks/useMaquinas";

import webpayPlus from "@/assets/imgs/oneclick.png";
import webpayTransbanck from "@/assets/imgs/tranbank-1.png";
import mercadoPagoLogo from "@/assets/imgs/mercadopago.png";

import { PayWithDiscountButton } from "./components/PayWithDiscountButton";
import bannerStore from "@/assets/imgs/Barquilleria.png";

export const CartPage = () => {
  const queryClient = useQueryClient();
  const { getCompanyRoute } = useCompanyFlow();
  const {
    total,
    products,
    productsByMachine,
    payMethod,
    setPayMethod,
    setDiscount,
    discount,
    calculateTotals,
    setBuyOrder,
    removeFromCart,
    setToPay,
    toPay,
  } = useProductStore();
  const { machineSelected, addressMachine, machine, nameEmpresa, logoMachine } =
    useMachine();
  const { statusAuth, tempCard, user, authType, setTempCard, setCards } =
    useAuth();
  const { setOrder } = useOrder();
  const validStockBolsa = useValidStockBolsa();
  const { mutateAsync: validStockMutateAsync } = validStockBolsa;
  const oneClickUser = useGetOneClickUser();
  const { comercioEfectivo, tarjetasValidas, tempCardValida } =
    useOneClickComercio();
  const transbankMethods = useGetTransbankMethods();
  const [paymentMethods, setPaymentMethods] = useState(null);
  const isMutatingValidStock = useIsMutating({
    mutationKey: ["useValidStockBolsa"],
  });
  const [stockResponse, setStockResponse] = useState([]);

  const productsInCart = useMemo(() => {
    return (
      _.filter(
        products,
        (item) => item.maquina_id == machineSelected && Number(item.producto_cant) > 0
      ) || []
    );
  }, [products, machineSelected]);

  const productsQant = useMemo(() => {
    return productsInCart.reduce((acumulador, producto) => {
      return acumulador + producto.producto_cant;
    }, 0);
  }, [productsInCart]);

  const [existProductOutStock, setExistProductOutStock] = useState(false);
  const [code, setCode] = useState("");
  const codigoDeDescuento = CodigoDeDescuento();
  const [discountData, setDiscountData] = useState(
    codigoDeDescuento.data || [],
  );
  const current = currentDate();
  const [codeError, setCodeError] = useState(false);
  const [codeErrorMsg, setCodeErrorMsg] = useState("");
  const [, setCodeDosxUno] = useState(0);
  const [, setDiscountValue] = useState(0);
  const [available, setAvailable] = useState(true);
  const [, setSubtotal] = useState(0);
  const ispromotionActive = useRef(false);
  const buttonApplyDiscountClassName = available
    ? "order-btn-outline"
    : "order-btn-outline-disable";
  const navigate = useNavigate();

  // Ref para acceder a discountData y available sin recrear el callback
  const discountDataRef = useRef(discountData);
  const availableRef = useRef(available);
  const codeRef = useRef(code);
  discountDataRef.current = discountData;
  availableRef.current = available;
  codeRef.current = code;

  // Detecta ofertas activas por producto para bloquear la aplicación de
  // cupones. El criterio vive en src/lib/exclusionCupon.js, unificado con
  // hasCartOffers() de Sima-appmovil y con los dos endpoints del backend que
  // deciden el cobro — ver ese módulo para por qué la oferta directa y el
  // descuento de máquina NO bloquean el cupón.
  // Se asigna el booleano completo (no sólo `true`) para que el ref no quede
  // "pegado" tras vaciar/reemplazar el producto que disparó el bloqueo.
  const checkPromo = useCallback(() => {
    let productsFiltered = _.filter(
      products,
      (item) => item.maquina_id == machineSelected && Number(item.producto_cant) > 0,
    );

    const hayOferta = hayOfertaQueExcluyeCupon(productsFiltered);

    ispromotionActive.current = hayOferta;
    return hayOferta;
  }, [products, machineSelected]);

  // Recalcula el monto del descuento localmente cuando cambian las cantidades.
  // useCallback con deps vacías: lee todo desde el store para evitar closure stale.
  const recalcularDescuento = useCallback((data) => {
    if (!data || _.size(data) === 0) return;

    const {
      total: subtotalBruto,
      productsByMachine: currentProducts,
      setDiscount: storeSetDiscount,
    } = useProductStore.getState();

    if (data.desc_tipo_nombre === "2x1") {
      if (data.productos == null) {
        // Emparejamiento global: igual que el backend (calcularDescuentoCarritoMP
        // con total_2x1). Se aplana la lista de precios de TODOS los productos
        // (no sólo los que tienen qty >= 2), se ordena de mayor a menor y se
        // regala uno de cada dos unidades. Esto evita el descuadre cuando hay
        // productos con qty=1 que el backend empareja con los de qty >= 2.
        const flat = [];
        currentProducts.forEach((product) => {
          const precio =
            product.descuento_precio_final > 0 &&
            product.producto_precio > product.descuento_precio_final
              ? product.descuento_precio_final
              : product.producto_oferta > 0 &&
                product.producto_oferta < product.producto_precio
              ? product.producto_oferta
              : product.producto_precio;
          for (let i = 0; i < product.producto_cant; i++) flat.push(precio);
        });
        flat.sort((a, b) => b - a);
        const total_discount = flat.reduce(
          (s, p, i) => (i % 2 === 1 ? s + p : s),
          0,
        );
        storeSetDiscount(total_discount, data.descuento_id, data);
      } else {
        // 2x1 a producto específico: 1 unidad gratis por cada 2 del MISMO
        // producto elegible (no por producto único como antes), sumado entre
        // todos los productos elegibles presentes en el carrito. Antes esta
        // rama no existía y el descuento quedaba sin recalcular al cambiar
        // cantidades después de aplicar el cupón.
        let total_discount = 0;
        data.productos.forEach((eligible) => {
          const product = currentProducts.find(
            (p) => p.producto_id === eligible.producto_id,
          );
          if (!product || product.producto_cant < 2) return;
          // No acumula con el 2x1 propio del producto (ya reduce lo cobrado).
          if (product.descuento_dosxuno > 0) return;

          const precio =
            product.descuento_precio_final > 0 &&
            product.producto_precio > product.descuento_precio_final
              ? product.descuento_precio_final
              : product.producto_oferta > 0 &&
                product.producto_oferta < product.producto_precio
              ? product.producto_oferta
              : product.producto_precio;
          const freeQty = Math.floor(product.producto_cant / 2);
          total_discount += precio * freeQty;
        });
        storeSetDiscount(total_discount, data.descuento_id, data);
      }
      return;
    }

    if (data.descuento_tipo_id === 2 && data.productos == null) {
      // Monto fijo sobre el total
      storeSetDiscount(data.descuento_valor, data.descuento_id, data);
      return;
    }

    if (data.descuento_tipo_id === 3 && data.productos == null) {
      // Porcentaje sobre el subtotal bruto actual
      storeSetDiscount(
        (subtotalBruto * data.descuento_valor) / 100,
        data.descuento_id,
        data,
      );
      return;
    }

    if (data.productos != null) {
      let total_discount = 0;
      currentProducts.forEach((product) => {
        data.productos.forEach((item) => {
          if (product.producto_id === item.producto_id) {
            if (data.descuento_tipo_id === 2) {
              total_discount +=
                product.producto_cant *
                Math.min(
                  data.descuento_valor,
                  product.producto_oferta > 0
                    ? product.producto_oferta
                    : product.producto_precio,
                );
            } else if (data.descuento_tipo_id === 3) {
              const base =
                product.producto_oferta > 0
                  ? product.producto_oferta
                  : product.producto_precio;
              total_discount +=
                ((base * data.descuento_valor) / 100) * product.producto_cant;
            }
          }
        });
      });
      storeSetDiscount(total_discount, data.descuento_id, data);
    }
  }, []);

  useEffect(() => {
    calculateTotals(machineSelected);

    if (products.length == 0) {
      ispromotionActive.current = false;
      setCode("");
      setCodeError(false);
      setCodeErrorMsg("");
      setDiscountValue(0);
      setDiscountData([]);
      setDiscount(0, 0, null);
      setAvailable(true);
    } else {
      const hayOferta = checkPromo();
      const cuponAplicado =
        codeRef.current &&
        !availableRef.current &&
        _.size(discountDataRef.current) > 0;

      if (hayOferta && cuponAplicado) {
        // Apareció una oferta activa (producto agregado con descuento de
        // máquina, cambio de máquina, etc.) después de aplicar un cupón:
        // limpiarlo en vez de dejarlo "zombi" mostrando un descuento que
        // crearPreferencia/createOrder van a ignorar de todas formas.
        setCode("");
        setCodeError(true);
        setCodeErrorMsg(
          "Tienes productos con ofertas aplicadas, no puedes usar cupones de descuento en este pedido.",
        );
        setDiscountValue(0);
        setDiscountData([]);
        setDiscount(0, 0, null);
        setAvailable(true);
      } else if (cuponAplicado) {
        // Cupón ya aplicado: recalcular el monto localmente usando el total fresco del store
        recalcularDescuento(discountDataRef.current);
      }
    }
  }, [
    products,
    machineSelected,
    calculateTotals,
    checkPromo,
    recalcularDescuento,
    setDiscount,
  ]);

  useEffect(() => {
    // Debounce para agrupar rápidos cambios de cantidad
    const handler = setTimeout(() => {
      const productsFiltered = _.filter(
        products,
        (item) => item.maquina_id == machineSelected,
      );
      let productsMap = _.map(productsFiltered, (item) => {
        return {
          ...item,
          inventario_slot: item.inventario_slot,
          maquina_id: item.maquina_id,
          producto_cant: item.producto_cant,
          producto_descripcion: item.producto_descripcion,
          producto_formato: `${item.cantidad} ${item.presentacion_nombre}`,
          producto_id: item.producto_id,
          producto_nombre: item.producto_nombre,
          producto_precio: item.producto_precio,
          producto_oferta: item.producto_oferta,
          cantidad: item.producto_cant,
          logo_empresa: item.logo_machine,
          distancia: "13.18",
          latitud: machine.maquina_latitud,
          longitud: machine.maquina_longitud,
          full_count: "161",
          maquina_direccion: machine.maquina_direccion,
          maquina_nombre: machine.maquina_nombre,
          empresa_id: machine.empresa_id,
        };
      });

      validStockMutateAsync({ dataBolsa: productsMap })
        .then((resp) => {
          if (resp?.status === "success" && resp?.stock) {
            setStockResponse([]);
            setExistProductOutStock(false);
            return;
          }
          const ids = resp?.productsId || [];
          setStockResponse(ids);
          setExistProductOutStock(ids.length > 0);
        })
        .catch(() => {
          setExistProductOutStock(true);
        });
    }, 250); // 250ms debounce

    return () => clearTimeout(handler);
  }, [products, machineSelected, machine, validStockMutateAsync]); // Revisar stock

  useEffect(() => {
    // Limpiar cache obsoleto de stock para no mostrar resultados de sesión anterior
    queryClient.removeQueries({ queryKey: ["stockBolsa"] });
    setDiscount(0, 0, null);
    setToPay(true);
    calculateTotals(machineSelected);
    //setAvailable(true);
    setPayMethod("");

    if (machineSelected) {
      transbankMethods
        .mutateAsync(machineSelected)
        .then((resp) => {
          setPaymentMethods(_.isArray(resp) ? resp[0] : null);
        })
        .catch(() => {
          setPaymentMethods(null);
        });
    }

    setOrder({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se ejecuta solo una vez al montar para inicializar el estado del carrito (resetea descuento/payMethod, carga métodos transbank); no debe re-ejecutarse ante cambios posteriores de estos valores
  }, []);

  // Carga de tarjetas OneClick del comercio efectivo vigente. Deps
  // primitivas (nunca el objeto `machine` ni `oneClickUser`, que no es
  // estable entre renders) para que sea reactivo al cambio de máquina sin
  // recargar (criterio 2) sin re-disparar la llamada en cada render.
  useEffect(() => {
    if (!(statusAuth == "authed" && authType == "user" && comercioEfectivo !== "")) {
      return;
    }

    const dataSend = {
      usuario_id: user.usuario_id,
      codigo_comercio: comercioEfectivo,
    };

    oneClickUser
      .mutateAsync(dataSend)
      .then((resp) => {
        const validas = filtrarTarjetasPorComercio(resp, comercioEfectivo);
        setCards(validas);
        setTempCard(validas.length > 0 ? validas[0] : {});
      })
      .catch((error) => {
        // Criterio 9: nunca conservar una lista de otro comercio ante un
        // fallo de carga.
        setCards([]);
        setTempCard({});
        Sentry.captureException(error, {
          tags: { payment_flow: "oneclick" },
          extra: {
            usuario_id: user?.usuario_id,
            codigo_comercio: comercioEfectivo,
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- oneClickUser (mutateAsync) no es estable entre renders; sólo debe reevaluarse cuando cambian estas deps primitivas
  }, [statusAuth, authType, user?.usuario_id, comercioEfectivo]);

  // Criterio 4: sanea un tempCard heredado/de otro comercio en cuanto el
  // comercio efectivo vigente cambia (dos pestañas, cambio de máquina).
  useEffect(() => {
    if (
      _.size(tempCard) > 0 &&
      !esTarjetaValidaParaComercio(tempCard, comercioEfectivo)
    ) {
      setTempCard({});
    }
    // setTempCard({}) deja _.size(tempCard) en 0 en la siguiente ejecución,
    // por lo que no hay loop pese a incluir tempCard en las deps.
  }, [comercioEfectivo, tempCard, setTempCard]);

  const handleChangePayMethod = (value) => {
    if (statusAuth != "authed")
      toast.error("Debe registrarse para realizar su compra.");

    if (value == "webpayTransbanck") {
      let buyOrder = "WP-" + Math.round(Math.random() * 999999999);
      setBuyOrder(buyOrder);
    }

    if (value == "oneclick") {
      let buyOrder = "Mall-" + Math.round(Math.random() * 999999999);
      setBuyOrder(buyOrder);
    }

    if (value == "mercadopago") {
      // buy_order es varchar(20) en BD y se reutiliza como external_reference en MP
      setBuyOrder(generarBuyOrderMP());
    }

    setPayMethod(value);
  };

  const handleClearDiscount = () => {
    setCode("");
    setCodeError(false);
    setCodeErrorMsg("");
    setDiscountValue(0);
    setDiscountData([]);
    setDiscount(0, 0, null);
    setAvailable(true);
  };

  const handleDiscountCode = () => {
    setDiscountValue(0);
    // Limpiar estado previo antes de la nueva consulta
    setCodeError(false);
    setCodeErrorMsg("");

    if (!code) return;
    if (productsByMachine.length === 0) {
      setCodeError(true);
      setCodeErrorMsg(
        "Debe agregar productos al carrito antes de aplicar un descuento.",
      );
      return;
    }

    codigoDeDescuento
      .mutateAsync(code)
      .then((data) => {
        if (data == null) {
          setCodeError(true);
          setCodeErrorMsg("Este código de descuento no es válido.");
          return;
        }

        checkPromo();
        if (ispromotionActive.current) {
          // Carrito con oferta activa (porcentaje, monto fijo o 2x1): no se
          // permite aplicar cupón, ni localmente ni en el flujo de createOrder.
          setCodeError(true);
          setCodeErrorMsg(
            "Tienes productos con ofertas aplicadas, no puedes usar cupones de descuento en este pedido.",
          );
          setDiscountValue(0);
          setDiscountData([]);
          setDiscount(0, 0, null);
          setAvailable(true);
          return;
        }

        setDiscountData(data);
        setAvailable(false);

        if (data.empresa_nombre != nameEmpresa) {
          //por nombre
          setCodeError(true);
          setCodeErrorMsg(
            "Este código de descuento no es válido para esta empresa.",
          );
          setAvailable(true);
          return;
        }

        if (data.descuento_limite_n_maximo_veces !== null) {
          if (
            _.size(data.usuariosDescuento) ==
            data.descuento_limite_n_maximo_veces
          ) {
            setCodeError(true);
            setCodeErrorMsg("Este cupón superó el límite de usos.");
            setDiscount(0);
            setAvailable(true);
            return;
          }

          if (_.size(data.usuariosDescuento) > 0) {
            data.usuariosDescuento.map((usuario) => {
              if (
                usuario.descuento_usado >= data.descuento_limite_n_maximo_veces
              ) {
                setCodeError(true);
                setCodeErrorMsg("Este cupón superó el límite de usos.");
                setAvailable(true);
                return;
              }
            });
          }
          if (codeError) return;
        }

        if (data.descuento_limite_uso_cliente !== false) {
          let error = false;
          data.usuariosDescuento.map((usuario) => {
            if (usuario.usuario_id == user.usuario_id) {
              error = true;
              setCodeError(true);
              setCodeErrorMsg(
                "Código de descuento ya utilizado por este cliente.",
              );
              setDiscount(0, data.descuento_id);
              setAvailable(true);
              return;
            }
          });

          if (error) return;
        }

        // Validar primera compra
        if (data.elegibilidad_cliente === "1") {
          if ((user.total_pedidos ?? 0) > 0) {
            setCodeError(true);
            setCodeErrorMsg(
              "Este cupón es válido solo para tu primera compra.",
            );
            setAvailable(true);
            return;
          }
        }

        if (
          processDate(data.descuento_fecha_fin) < processDate(current) ||
          processDate(data.descuento_fecha_inicio) > processDate(current)
        ) {
          //por fecha
          setCodeError(true);
          setCodeErrorMsg("Este código de descuento ha caducado.");
          setAvailable(true);
          return;
        }

        if (
          data.descuento_req_min_valor != null &&
          data.descuento_req_min_valor > 0
        ) {
          //requerimiento minimo
          const currentTotal = useProductStore.getState().total;
          if (
            currentTotal < data.descuento_req_min_valor &&
            productsByMachine.length > 0
          ) {
            setCodeError(true);
            setCodeErrorMsg(
              "Su compra debe ser mayor a $ " + data.descuento_req_min_valor,
            );
            setAvailable(true);
            return;
          }
        }

        if (data.desc_tipo_nombre == "2x1") {
          //2x1

          let itemValid = false;
          if (data.productos == null) {
            // Necesitamos al menos 2 unidades en total para formar un par 2x1.
            const totalUnidades = productsByMachine.reduce(
              (sum, p) => sum + p.producto_cant,
              0,
            );
            if (totalUnidades < 2) {
              setCodeError(true);
              setCodeErrorMsg(
                "Este cupón no aplica para la cantidad de sus productos.",
              );
              setDiscount(0, data.descuento_id, data);
              setAvailable(true);
              return;
            }

            // Emparejamiento global: igual que el backend (calcularDescuentoCarritoMP
            // con total_2x1). Se aplana la lista de precios unitarios de TODOS los
            // productos, se ordena de mayor a menor y se regala uno de cada dos
            // unidades. Esto corrige el descuadre que ocurría cuando el frontend
            // usaba emparejamiento por-producto (1 gratis por cada producto con
            // qty>=2) mientras el backend emparejaba globalmente (incluyendo
            // productos con qty=1 como parte de pares entre productos distintos).
            const flat = [];
            productsByMachine.forEach((product) => {
              const precio =
                product.descuento_precio_final > 0 &&
                product.producto_precio > product.descuento_precio_final
                  ? product.descuento_precio_final
                  : product.producto_oferta > 0 &&
                    product.producto_oferta < product.producto_precio
                  ? product.producto_oferta
                  : product.producto_precio;
              for (let i = 0; i < product.producto_cant; i++) flat.push(precio);
            });
            flat.sort((a, b) => b - a);
            const total_discount = flat.reduce(
              (s, p, i) => (i % 2 === 1 ? s + p : s),
              0,
            );

            setDiscount(total_discount, data.descuento_id, data);
            itemValid = true;
          } else if (data.productos != null) {
            // 1 unidad gratis por cada 2 del MISMO producto elegible, sumado
            // entre todos los productos elegibles del carrito (antes solo se
            // regalaba 1 unidad total sin importar la cantidad, y el último
            // producto que matcheaba sobrescribía el descuento de los demás).
            let total_discount = 0;
            data.productos.forEach((eligible) => {
              const product = productsByMachine.find(
                (p) => p.producto_id == eligible.producto_id,
              );
              if (!product || product.producto_cant < 2) return;
              // No acumula con el 2x1 propio del producto (ya reduce lo cobrado).
              if (product.descuento_dosxuno > 0) return;

              const precio =
                product.descuento_precio_final > 0 &&
                product.producto_precio > product.descuento_precio_final
                  ? product.descuento_precio_final
                  : product.producto_oferta > 0 &&
                    product.producto_oferta < product.producto_precio
                  ? product.producto_oferta
                  : product.producto_precio;
              const freeQty = Math.floor(product.producto_cant / 2);
              total_discount += precio * freeQty;
              setCodeDosxUno(precio);
              itemValid = true;
            });

            if (itemValid) {
              setDiscount(total_discount, data.descuento_id, data);
            }
          }

          if (!itemValid) {
            setCodeError(true);
            setCodeErrorMsg(
              "Este cupón no aplica para su(s) producto(s) o la cantidad.",
            );
            setDiscount(0, data.descuento_id, data);
            return;
          }
          return;
        }

        if (data.descuento_tipo_id == 2 && data.productos == null) {
          setSubtotal(total);
          setDiscount(data.descuento_valor, data.descuento_id, data);

          // }
          // else{
          //   setCodeError(true);
          //   setCodeErrorMsg("El total de tu compra debe ser mayor al descuento");
          // }

          return;
        }

        if (data.descuento_tipo_id == 3 && data.productos == null) {
          // productsInCart.map((product)=>{
          //   if(product.producto_oferta > 0)
          //     sum += ((product.producto_oferta*data.descuento_valor/100)*product.producto_cant);
          //   else
          //     sum += ((product.producto_precio*data.descuento_valor/100)*product.producto_cant);

          // })

          setDiscount(
            (total * data.descuento_valor) / 100,
            data.descuento_id,
            data,
          );

          return;
        }

        if (data.productos != null) {
          let productValid = false;
          let total_discount = 0;

          productsByMachine.map((product) => {
            data.productos.map((item) => {
              if (product.producto_id == item.producto_id) {
                if (data.descuento_tipo_id == 2) {
                  if (
                    data.descuento_valor < product.producto_precio ||
                    data.descuento_valor < product.producto_oferta
                  ) {
                    total_discount +=
                      product.producto_cant * data.descuento_valor;
                  } else {
                    //si el valor del descuento es mayor al precio
                    if (product.producto_oferta > 0)
                      total_discount +=
                        product.producto_cant * product.producto_oferta;
                    else
                      total_discount +=
                        product.producto_cant * product.producto_precio;
                  }
                } else if (data.descuento_tipo_id == 3) {
                  //total_discount = (total*data.descuento_valor/100);
                  if (product.producto_oferta > 0)
                    total_discount +=
                      ((product.producto_oferta * data.descuento_valor) / 100) *
                      product.producto_cant;
                  else
                    total_discount +=
                      ((product.producto_precio * data.descuento_valor) / 100) *
                      product.producto_cant;
                }
                productValid = true;
              }
            });
          });
          if (!productValid) {
            setCodeError(true);
            setCodeErrorMsg(
              "Este cupón de descuento solo aplica para algunos productos. ",
            );
            setAvailable(true);
            return;
          }
          //identificar cual discount es para el pagar
          setDiscount(total_discount, data.descuento_id, data);
          setDiscountValue(total_discount);
          return;
        }
      })
      .catch((error) => {
        // Incidencia #8: manejar error del servicio de descuento
        console.error("Error al validar código de descuento:", error);
        setCodeError(true);
        setCodeErrorMsg(
          "Error al validar el código de descuento. Por favor, intente nuevamente.",
        );
        setAvailable(true);
      });
  };

  const handleVaciarCarro = () => {
    productsInCart.map((producto) => {
      removeFromCart(producto.producto_id, machineSelected);
    });
    calculateTotals(machineSelected);
    setDiscount(0, 0, null);
    //navigate(`/store/${machineSelected}`, { replace: false });
    goBack();
    //validStock();
  };

  const HeaderImage = () => {
    return (
      <div className="header-image">
        <img src={bannerStore} />
      </div>
    );
  };

  const BrandInfo = ({ logoUrl, name, address, distance }) => {
    return (
      <div className="brand-info">
        <div className="brand-details">
          <img src={logoUrl} alt={`${name} logo`} className="brand-logo" />
          <div>
            <h2 className="brand-name">{name}</h2>
            <div className="brand-location">
              <i className="icon-location" />
              <span>
                {distance} - {address}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const MetodoDePago = () => {
    return (
      <div className="col-12 col-sm-8 col-md-8 col-lg-8 mb-3 inter-regular">
        {/* <FormUser /> */}
        {/* <ViewMap /> */}
        {total > 0 ? (
          <div className="col-12 mb-3 ">
            <h5 className="text-black inter-semibold mt-3">
              Selecciona tu método de pago
            </h5>
          </div>
        ) : (
          <></>
        )}
        {!_.isEmpty(machine.webpay_codigo_comercio) &&
          paymentMethods?.webpay &&
          total > 0 && (
            <div className="col-12 inter-regular text-black font16 border border-1 rounded mb-3 p-3 d-flex">
              <div className="p-3">
                <Form.Check
                  type="radio"
                  id="motodoPago-2"
                  name="motodoPago"
                  checked={payMethod === "webpayTransbanck"}
                  onChange={() => handleChangePayMethod("webpayTransbanck")}
                />
              </div>
              <img
                src={webpayTransbanck}
                alt="webpayTransbanck"
                width="200"
                onClick={() => handleChangePayMethod("webpayTransbanck")}
              />
            </div>
          )}

        {comercioEfectivo !== "" &&
          paymentMethods?.onepay &&
          !_.isEmpty(machine?.oneclick_codigo_tienda) &&
          total > 0 && (
            <div className="col-12 inter-regular text-black font16 border border-1 rounded mb-3 p-3 d-flex">
              <div className="p-3">
                <Form.Check
                  type="radio"
                  id="motodoPago-1"
                  name="motodoPago"
                  checked={payMethod === "oneclick"}
                  onChange={() => handleChangePayMethod("oneclick")}
                />
              </div>
              <img
                src={webpayPlus}
                alt="webpayPlus"
                width="200"
                onClick={() => handleChangePayMethod("oneclick")}
              />
            </div>
          )}

        {paymentMethods?.mercadopago && total > 0 && (
          <div className="col-12 inter-regular text-black font16 border border-1 rounded mb-3 p-3 d-flex">
            <div className="p-3">
              <Form.Check
                type="radio"
                id="motodoPago-3"
                name="motodoPago"
                checked={payMethod === "mercadopago"}
                onChange={() => handleChangePayMethod("mercadopago")}
              />
            </div>
            <img
              src={mercadoPagoLogo}
              alt="mercadopago"
              width="200"
              onClick={() => handleChangePayMethod("mercadopago")}
            />
          </div>
        )}

        {payMethod === "oneclick" &&
          statusAuth == "authed" &&
          tarjetasValidas.length === 0 &&
          comercioEfectivo !== "" &&
          total > 0 && <AddCardOneClick />}
        {payMethod == "oneclick" &&
          tarjetasValidas.length > 0 &&
          comercioEfectivo !== "" &&
          total > 0 && <CardOneClick />}
      </div>
    );
  };

  const goBack = () => {
    navigate(getCompanyRoute(`store/${machineSelected}`), { replace: true });
  };

  const handleVolver = () => {
    if (!toPay) setToPay(true);
    else goBack();
  };

  return (
    <section id="pago" className="container my-5" style={{ flexGrow: 1 }}>
      <SEO title="Mi carrito" noIndex={true} />
      <Skeleton loading={isMutatingValidStock} active>
        <HeaderImage />
        <div className="brand-header">
          <BrandInfo
            logoUrl={logoMachine}
            name={nameEmpresa}
            address={addressMachine}
            distance={""}
          />
        </div>
        <div className="row">
          {toPay || total == 0 ? (
            <div className="col-12 col-sm-8 col-md-8 col-lg-8">
              <div className="">
                <div id="currentOrder" className="col-12"></div>
                {productsInCart.map((item) => {
                  return (
                    <ProductsComponent
                      key={item.producto_id}
                      product={item}
                      stockBolsa={stockResponse}
                    />
                  );
                })}

                {/* <div className="col-12 d-flex mb-3" style={{paddingLeft:"0px", paddingRight:"0px"}}>
                  <div className="col-10">
                  <input type="text" id="quantityInput" className="form-control" value={code}
                  placeholder="Ingrese un código de descuento"  onChange={(e) => setCode(e.target.value)}/>
                  </div>
                  <div className="col-2">
                  <Button className="inter-semibold mx-1" style={{width:"100%"}} variant="secondary" size="md" 
                  onClick={handleDiscountCode} disabled={!available} >
                    aplicar
                  </Button>
                  </div>
                </div> */}

                {/* AQUI ESTA LA SECCION PARA VER EL DESCUENTO FUNCIONANDO */}
                {/* <div className="col-12">
                {_.size(discountData) > 0 &&
                !codeError &&
                (codeDosxUno > 0 || discountData.descuento_valor != null) ? (
                  <>
                    <div
                      className="row inter-semibold text-black"
                      style={{ fontSize: 20 }}
                    >
                      <div className="col-6 text-start">
                        <div className="col-12 text-start">
                          {products.length > 0 ? <p>Subtotal</p> : <></>}
                        </div>

                        {products.length > 0 ? (
                          <div className="col-12 text-start">
                            <p className="font14 inter-regular">Descuento </p>
                          </div>
                        ) : (
                          <></>
                        )}

                        <div className="col-12 text-start">
                          <p>Total </p>
                        </div>
                      </div>

                      <div className="col-6 text-end">
                        {products.length == 0 ? (
                          <></>
                        ) : discountData.descuento_valor > total &&
                          total == 0 ? (
                          <div className="col-12 text-end text-red">
                            <p>{"$" + subtotal}</p>
                          </div>
                        ) : (
                          <div className="col-12 text-end text-red">
                            <p>{formatMoney(total)}</p>
                          </div>
                        )}

                        <div className="col-12 text-end">
                          {products.length == 0 ? (
                            <></>
                          ) : discountData.descuento_valor > subtotal &&
                            subtotal != 0 ? (
                            <p>{"- $" + subtotal}</p>
                          ) : discountData.descuento_tipo_simbolo == "" ? (
                            <p>{" - " + formatMoney(discount)}</p>
                          ) : discountData.descuento_tipo_id == 2 &&
                            discountData.productos != null ? (
                            <p>{" - $" + discount}</p>
                          ) : discountData.descuento_tipo_simbolo == "$" ? (
                            <p>
                              {" - " +
                                discountData.descuento_tipo_simbolo +
                                discountData.descuento_valor}
                            </p>
                          ) : (
                            <p>
                              {" - " +
                                discountData.descuento_valor +
                                discountData.descuento_tipo_simbolo}
                            </p>
                          )}
                        </div>
                        <div className="col-12 text-end">
                          <p>{formatMoney(Math.max(0, total - discount))}</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className="row inter-semibold text-black"
                    style={{ fontSize: 20 }}
                  >
                     <div className="col-6 text-start">
                      <div className="col-12 text-start">
                        <p>Subtotal !!</p>
                      </div>
                      {codeError ? (
                        <div className="col-12 text-start">
                          <p className="font14 inter-regular">{codeErrorMsg}</p>
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                    <div className="col-6 text-end">
                      <p>{formatMoney(total)}</p>
                    </div> 
                  </div>
                )} 
              </div> */}
                {/* AQUI ESTA LA SECCION PARA VER EL DESCUENTO FUNCIONANDO */}
                <hr />
                <br />
                <div className="col-12">
                  <div className="row inter-semibold ">
                    <div className="col-6 text-start"></div>
                    <div className="col-6 text-end">
                      <Link to={getCompanyRoute(`store/${machineSelected}`)}>
                        <button className="order-btn-outline">
                          Agregar artículos
                        </button>
                      </Link>
                    </div>
                    {/* <div className="col-6 text-end">
                    {payMethod === "webpayTransbanck" &&
                      statusAuth == "authed" &&
                      !existProductOutStock &&
                      _.size(products) > 0 &&
                      total > 0 && <WebPayButton />}
                    {payMethod === "oneclick" &&
                      statusAuth == "authed" &&
                      _.size(tempCard) > 0 &&
                      !existProductOutStock &&
                      _.size(products) > 0 &&
                      total > 0 && <OneClickButton />}

                    {total == 0 &&
                      statusAuth == "authed" &&
                      !existProductOutStock &&
                      _.size(products) > 0 && <PayWithDiscountButton />}
                  </div> */}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <MetodoDePago />
          )}
          <div className="col-12 col-sm-4 col-md-4 col-lg-4">
            {/* <OrderSummary totalPrice={subtotal} discountCode={discount}/> */}
            <div className="order-summary">
              <div className="order-discount">
                <p className="order-label">Ingresa un cupón de descuento</p>
                <div className="d-flex">
                  <div className="order-discount-input-wrapper">
                    <span className="order-discount-icon">🎁</span>
                    <input
                      type="text"
                      placeholder="Código de descuento"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      disabled={!available}
                    />
                  </div>
                  {!available ? (
                    <button
                      className="order-btn-outline"
                      onClick={handleClearDiscount}
                      title="Quitar cupón"
                    >
                      ✕
                    </button>
                  ) : (
                    <button
                      className={buttonApplyDiscountClassName}
                      onClick={handleDiscountCode}
                      disabled={!available}
                    >
                      Aplicar
                    </button>
                  )}
                </div>
                {codeError && (
                  <span className="tag-span-error">{codeErrorMsg}</span>
                )}
              </div>

              <div className="order-summary-details">
                <h3>Resumen del pedido</h3>

                <span>{productsQant} artículo(s)</span>
                <br />
                {_.size(discountData) > 0 && (
                  <>
                    <div className="order-summary-total">
                      <p className="order-items">
                        {products.length > 0 ? <span>Subtotal</span> : <></>}
                      </p>
                      {products.length == 0 ? (
                        <></>
                      ) : (
                        <p className="order-price" style={{ color: "#939393" }}>
                          {formatMoney(total)} CLP
                        </p>
                      )}
                    </div>
                    {!codeError && (
                      <div className="order-summary-total">
                        <p className="order-items">
                          {products.length > 0 ? (
                            <span>
                              Descuento [{discountData.descuento_nombre}]
                            </span>
                          ) : (
                            <></>
                          )}
                        </p>
                        <p className="order-price" style={{ color: "#34A853" }}>
                          {"-" + formatMoney(discount)} CLP
                        </p>
                      </div>
                    )}
                  </>
                )}
                <div className="order-summary-total">
                  <p className="order-items">Total</p>
                  <p className="order-price">
                    {formatMoney(Math.max(0, total - discount))} CLP
                  </p>
                </div>
                <div className="order-buttons">
                  {existProductOutStock && (
                    <span className="cart-out-stock">
                      Tienes productos sin stock
                    </span>
                  )}
                </div>
                <div className="order-buttons">
                  <button
                    className="order-btn-outline"
                    //onClick={() => goBack()}
                    onClick={() => handleVolver()}
                  >
                    Volver
                  </button>
                  {toPay ? (
                    !existProductOutStock &&
                    (total - discount <= 0 && total > 0 ? (
                      <PayWithDiscountButton />
                    ) : (
                      <button
                        className="order-btn-fill"
                        onClick={() => setToPay(false)}
                      >
                        Ir a pagar
                      </button>
                    ))
                  ) : (
                    <div className="">
                      {payMethod === "webpayTransbanck" &&
                        statusAuth == "authed" &&
                        !existProductOutStock &&
                        _.size(products) > 0 &&
                        total > 0 &&
                        total - discount > 0 && <WebPayButton />}
                      {payMethod === "oneclick" &&
                        statusAuth == "authed" &&
                        tempCardValida &&
                        !existProductOutStock &&
                        _.size(products) > 0 &&
                        total > 0 &&
                        total - discount > 0 && <OneClickButton />}
                      {payMethod === "mercadopago" &&
                        statusAuth == "authed" &&
                        !existProductOutStock &&
                        _.size(products) > 0 &&
                        total > 0 &&
                        total - discount > 0 && <MercadoPagoButton />}

                      {total - discount <= 0 &&
                        total > 0 &&
                        statusAuth == "authed" &&
                        !existProductOutStock &&
                        _.size(products) > 0 && <PayWithDiscountButton />}
                    </div>
                  )}
                </div>
                {toPay && (
                  <button
                    className="order-clear-cart"
                    onClick={() => handleVaciarCarro()}
                  >
                    Vaciar carro
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* <MetodoDePago /> */}
        </div>
      </Skeleton>
    </section>
  );
};
